import { create } from "zustand"
import {
	LocationProps,
	ControlsGeoType,
	ControlsViewType,
	ControlsStatType,
	ExtentsProp,
	ExtentsProps,
} from "@/utils/types";
import { createExtents, fetchData, getLocationName, getLocationStateFips, getLocationType, getValue } from "@/utils/helpers";
import { getLang } from "@/utils/selectors";

const MAX_STORE_MB = 20
const EST_ROWS_PER_MB = 2400

const estimateMb = (rows: []) =>
	rows.length / EST_ROWS_PER_MB;

const estimateTotalMb = (data: object) =>
	Math.round(Object.values(data).reduce((sum, rows) => sum + estimateMb(rows), 0) * 100) / 100;

export interface DataStoreProps {
	locations: {
		[geo in ControlsGeoType]?: {
			[state: string]: LocationProps[]
		}
	};
	getLocation: (id: string) => LocationProps | null;
	getLocations: (geo: ControlsGeoType, state: string) => LocationProps[];
	getGeoLocations: (geo: ControlsGeoType) => LocationProps[];
	setLocations: (geo: ControlsGeoType, state: string, locations: LocationProps[]) => void;
	fetchLocations: (geo: ControlsGeoType, state: string, callback?: () => void) => void;
	extents: ExtentsProps | [] | null;
	setExtents: (extents: ExtentsProps) => void;
	getExtents: (geo: ControlsGeoType, view: ControlsViewType, stat: ControlsStatType) => number[];
	fetchExtents: () => void;
	dataLoading: boolean;
	setDataLoading: (dataLoading: boolean) => void;
}

export const useDataStore = create<DataStoreProps>()(
	(set, get) => ({
		extents: null,
		setExtents: extents => set(() => ({ extents })),
		getExtents: (geo, view, stat) => {
			const extents = get().extents?.find((extent) =>
				extent.geo === geo
				&& extent.view === view
				&& extent.stat === stat
			)
			return createExtents([extents?.min, extents?.max])
		},
		fetchExtents: async () => {
			// If extents already exist, skip
			if(get().extents?.length) return;
			// Fetch extents from remote source
			const extents: ExtentsProp[] = await fetchData("extents");
			get().setExtents(extents);
		},
		states: [],
		locations: {},
		getLocation: (id) => {
			const geo = getLocationType(id);
			const state = getLocationStateFips(id);
			return get().getLocations(geo, state)?.find(location => id === location?.id) ?? null
		},
		getLocations: (geo, state) =>
			getValue(state, get().getGeoLocations(geo))
		,
		getGeoLocations: (geo) =>
			getValue(geo, get().locations)
		,
		setLocations: (geo, state, locations) => {
			const locationsByState = getValue(geo, get().locations) ?? {};
			console.log(`Currently has ${Object.keys(locationsByState).length} states with ${geo} locations [${estimateTotalMb(locationsByState)}MB / ${MAX_STORE_MB}MB]`)
			let newLocationsByState: Record<string, LocationProps[]> = {
				...locationsByState,
				[state]: locations
			};
			console.log(`Adding ${getLang("state", state)}'s ${geo} locations [${estimateTotalMb(newLocationsByState)}MB / ${MAX_STORE_MB}MB]`)
			// Evict largest states (excluding the new one) until under memory limit
			while (estimateTotalMb(newLocationsByState) > MAX_STORE_MB) {
				const evictable = Object.entries(newLocationsByState)
					.filter(([id]) => id !== state)
					.sort(([, a], [, b]) => b.length - a.length);

				if (evictable.length === 0) break;

				const [largestId] = evictable[0];
				const { [largestId]: _largest, ...rest } = newLocationsByState;
				newLocationsByState = rest;
				console.log(`Removing ${getLocationName(geo, largestId)}'s ${geo} locations [${estimateTotalMb(rest)}MB / ${MAX_STORE_MB}MB]`)
			}

			set(() => ({
				locations: {
					...get().locations,
					[geo]: newLocationsByState
				}
			}))
		},
		fetchLocations: async (geo, state, callback) => {
			// If locations already exist, skip fetching
			if(get().getLocations(geo, state)?.length) {
				if(callback) callback();
			} else {
				const locations: LocationProps[] = await fetchData(`states/${geo}/${state}`);
				get().setLocations(geo, state, locations);
				if(callback) callback();
			}
		},
		dataLoading: false,
		setDataLoading: dataLoading => set({ dataLoading })
	})
)